import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useCustomerAuthStore } from '../../stores/useCustomerAuthStore';
import { toast } from 'react-hot-toast';
import { BOOKING_BACKEND_URL } from '../../services/api';

const AccountPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { customer, isAuthenticated, isLoading, logout, token, updateCustomer } = useCustomerAuth();
  const { bookings, isLoadingBookings, setBookings, setLoadingBookings } = useCustomerAuthStore();

  // 編輯資料對話框狀態
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
  const [isUpdating, setIsUpdating] = useState(false);

  // 變更密碼對話框狀態
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // 標記是否正在登出，防止顯示「請先登入」訊息
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // 檢查是否已登入
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isLoggingOut) {
      toast.error(t('pages:booking.account.pleaseLogin'));
      navigate(`/book/${storeId}/login`);
    }
  }, [isAuthenticated, isLoading, isLoggingOut, navigate, storeId, t]);

  // 載入預約列表（帶快取）
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchBookings();
    }
  }, [isAuthenticated, token]);

  const fetchBookings = async () => {
    try {
      // 如果已經有快取資料，不顯示載入狀態
      const hasExistingData = bookings.length > 0;
      setLoadingBookings(!hasExistingData);

      const response = await fetch(
        `${BOOKING_BACKEND_URL}/api/customers/bookings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error(t('pages:booking.account.loadBookingsError'));
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleLogout = () => {
    setIsLoggingOut(true);
    logout();
    toast.success(t('pages:booking.account.logoutSuccess'));
    navigate(`/book/${storeId}`);
  };

  const handleOpenEditDialog = () => {
    setEditForm({
      name: customer?.name || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsUpdating(true);
      const response = await fetch(
        `${BOOKING_BACKEND_URL}/api/customers/profile`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editForm),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '更新失敗');
      }

      const updatedProfile = await response.json();
      updateCustomer(updatedProfile);
      toast.success(t('pages:booking.account.editDialog.updateSuccess'));
      setShowEditDialog(false);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      toast.error(error.message || t('pages:booking.account.editDialog.updateFailed'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('pages:booking.account.passwordDialog.mismatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error(t('pages:booking.account.passwordDialog.minLengthError'));
      return;
    }

    try {
      setIsChangingPassword(true);
      const response = await fetch(
        `${BOOKING_BACKEND_URL}/api/customers/profile/password`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            oldPassword: passwordForm.oldPassword,
            newPassword: passwordForm.newPassword,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || t('pages:booking.account.passwordDialog.changeFailed'));
      }

      toast.success(t('pages:booking.account.passwordDialog.changeSuccess'));
      setShowPasswordDialog(false);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      console.error('Failed to change password:', error);
      toast.error(error.message || t('pages:booking.account.passwordDialog.changeFailed'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { label: t('translation:bookingStatus.PENDING'), className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
      CONFIRMED: { label: t('translation:bookingStatus.CONFIRMED'), className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      CANCELLED: { label: t('translation:bookingStatus.CANCELLED'), className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' },
      COMPLETED: { label: t('translation:bookingStatus.COMPLETED'), className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      label: status,
      className: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300',
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-semibold ${config.className}`}
      >
        {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.account.backToHome')}</span>
        </button>

        {/* 個人資料卡片 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {t('pages:booking.account.title')}
            </h1>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
            >
              {t('pages:booking.account.logout')}
            </button>
          </div>

          <div className="space-y-3 mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.account.name')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{customer.name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.account.email')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">{customer.email}</span>
            </div>
            {customer.phone && (
              <div className="flex items-center space-x-2">
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.account.phone')}</span>
                <span className="font-semibold text-gray-900 dark:text-white">{customer.phone}</span>
              </div>
            )}
          </div>

          {/* 操作按鈕 */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-slate-700">
            <button
              onClick={handleOpenEditDialog}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-semibold transition-colors"
            >
              {t('pages:booking.account.editProfile')}
            </button>
            <button
              onClick={() => setShowPasswordDialog(true)}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-semibold transition-colors"
            >
              {t('pages:booking.account.changePassword')}
            </button>
          </div>
        </div>

        {/* 預約列表 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            {t('pages:booking.account.myBookings')}
          </h2>

          {isLoadingBookings ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            </div>
          ) : bookings.length > 0 ? (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div
                  key={booking.id}
                  onClick={() => navigate(`/book/${storeId}/my-bookings/${booking.managementToken}`)}
                  className="border-2 border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:border-brand-500 dark:hover:border-brand-500 transition-colors cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {booking.service.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('pages:booking.account.bookingNumber')}{booking.id}
                      </p>
                    </div>
                    {getStatusBadge(booking.status)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.account.bookingTime')}</span>
                      <span className="font-semibold text-gray-900 dark:text-white ml-2">
                        {new Date(booking.date).toLocaleString('zh-TW')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.account.serviceDuration')}</span>
                      <span className="font-semibold text-gray-900 dark:text-white ml-2">
                        {booking.duration} {t('pages:booking.account.minutes')}
                      </span>
                    </div>
                    {booking.staff && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.account.staffMember')}</span>
                        <span className="font-semibold text-gray-900 dark:text-white ml-2">
                          {booking.staff.name}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.account.servicePrice')}</span>
                      <span className="font-semibold text-gray-900 dark:text-white ml-2 text-lg">
                        ${Math.floor(booking.price)}
                      </span>
                    </div>
                  </div>

                  {booking.note && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-700">
                      <span className="text-gray-600 dark:text-gray-400 text-sm">{t('pages:booking.account.note')}</span>
                      <p className="text-gray-900 dark:text-white mt-1">{booking.note}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📅</div>
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {t('pages:booking.account.noBookings')}
              </p>
              <button
                onClick={() => navigate(`/book/${storeId}`)}
                className="mt-4 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold transition-colors"
              >
                {t('pages:booking.account.bookNow')}
              </button>
            </div>
          )}
        </div>

        {/* 編輯資料對話框 */}
        {showEditDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('pages:booking.account.editDialog.title')}
                </h2>

                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('pages:booking.account.editDialog.name')}
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-slate-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('pages:booking.account.editDialog.email')}
                    </label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-slate-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('pages:booking.account.editDialog.phone')}
                    </label>
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-slate-700 dark:text-white"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditDialog(false)}
                      disabled={isUpdating}
                      className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                    >
                      {t('pages:booking.account.editDialog.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={isUpdating}
                      className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                      {isUpdating ? t('pages:booking.account.editDialog.saving') : t('pages:booking.account.editDialog.save')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* 變更密碼對話框 */}
        {showPasswordDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t('pages:booking.account.passwordDialog.title')}
                </h2>

                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('pages:booking.account.passwordDialog.oldPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordForm.oldPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-slate-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('pages:booking.account.passwordDialog.newPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-slate-700 dark:text-white"
                      required
                      minLength={6}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('pages:booking.account.passwordDialog.minLength')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('pages:booking.account.passwordDialog.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-slate-700 dark:text-white"
                      required
                      minLength={6}
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordDialog(false);
                        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                      }}
                      disabled={isChangingPassword}
                      className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors disabled:opacity-50"
                    >
                      {t('pages:booking.account.passwordDialog.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={isChangingPassword}
                      className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                      {isChangingPassword ? t('pages:booking.account.passwordDialog.changing') : t('pages:booking.account.passwordDialog.change')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
